import React, { useState } from "react";
import { useDispatch, useStore } from "react-redux";

import { fetchAccountMetadata } from "@/actions/environments";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { Input } from "@/design-system/components/Input/Input";
import { Radio, RadioGroup } from "@/design-system/components/Radio/Radio";
import { Select } from "@/design-system/components/Select/Select";
import { toast } from "@/design-system/components/Toast/Toast";
import useCurrentEnvironment from "@/hooks/use-environment";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { FreshpaintState } from "@/reducers";
import { EditableProject } from "@/reducers/environments";
import { makeRequest, masqueradeToken } from "@/util";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { NEW_PROJECT_ENVIRONMENTS, NO_ADMIN_ACCESS_REASON } from "../constants";
import { EnvironmentCard } from "../EnvironmentCard";
import {
  buildSaveProjectRequest,
  createdFromResponse,
  findCreatedEnvironment,
  SaveProjectResponse,
  validateName,
} from "../saveProject";
import { AddEnvironmentStepProps, AddTarget } from "../types";
import { useCanAddEnvironments } from "../useCanAddEnvironments";

export function ChooseStep({
  data,
  updateData,
  goNext,
}: AddEnvironmentStepProps) {
  const dispatch = useDispatch();
  const store = useStore<FreshpaintState>();
  const { projects } = useAccountMetadata();
  const currentEnvironment = useCurrentEnvironment();
  const canAdd = useCanAddEnvironments();
  const [nameError, setNameError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [masqueradeConfirmed, setMasqueradeConfirmed] = useState(false);
  const masquerading = masqueradeToken() !== null;

  const projectID = data.projectID ?? currentEnvironment?.project.ID ?? null;
  const project = projects.find((candidate) => candidate.ID === projectID);
  const projectMissing =
    data.target === "environment" &&
    data.projectID !== null &&
    projects.length > 0 &&
    !project;
  const trimmedName = data.name.trim();

  if (data.created) {
    return (
      <WizardStepLayout
        title={`${data.created.envName} is ready`}
        description="Next, switch to it so the snippet you install and the events you check belong to it."
        primaryAction={{ label: "Next", onClick: goNext }}
      >
        <EnvironmentCard
          label="Created"
          projectName={data.created.projectName}
          envName={data.created.envName}
          highlighted
          isNew
        />
      </WizardStepLayout>
    );
  }

  async function loadProjects(): Promise<EditableProject[]> {
    await dispatch(fetchAccountMetadata());
    return store.getState().environments.accountMetadata.projects;
  }

  async function create() {
    setSaving(true);
    setSaveError(null);
    // Projects may have been renamed or deleted since this tab loaded them.
    const before = await loadProjects();
    const freshProject = before.find((candidate) => candidate.ID === projectID);
    if (data.target === "environment" && !freshProject) {
      updateData({ projectID: null });
      setNameError(null);
      setSaveError(
        "The project you chose no longer exists. Choose another project and try again.",
      );
      setSaving(false);
      return;
    }
    const error = validateName(data.target, data.name, before, freshProject);
    setNameError(error);
    if (error) {
      setSaving(false);
      return;
    }
    const request = buildSaveProjectRequest(
      data.target === "project" || !freshProject
        ? { target: "project", name: data.name }
        : { target: "environment", name: data.name, project: freshProject },
    );
    try {
      const response = await makeRequest<SaveProjectResponse>(
        "save_project",
        request,
      );
      await loadProjects();
      const created = createdFromResponse(
        response.data,
        request,
        freshProject?.name ?? request.name,
      );
      if (!created) {
        setSaveError(
          `${trimmedName} was created, but it didn’t load. Refresh the page, then choose it from the environment picker at the top of the sidebar.`,
        );
        setSaving(false);
        return;
      }
      updateData({ created });
      goNext();
    } catch (requestError) {
      // The save can land even when the request reports an error, and
      // retrying would then create a duplicate.
      const created = findCreatedEnvironment(
        before,
        await loadProjects(),
        request,
      );
      if (created) {
        toast.show(
          `${created.envName} was created, though Freshpaint reported an error. Check its settings before you use it.`,
        );
        updateData({ created });
        goNext();
        return;
      }
      setSaveError(unpackRequestErrorMessage(requestError));
      setSaving(false);
    }
  }

  const isProject = data.target === "project";

  return (
    <WizardStepLayout
      title="What do you want to add?"
      description="Each environment has its own snippet, events and destinations, so test traffic stays separate from your live site."
      primaryAction={{
        label: isProject ? "Create project" : "Create environment",
        onClick: create,
        loading: saving,
        disabled: canAdd === false || (masquerading && !masqueradeConfirmed),
        disabledReason:
          canAdd === false
            ? NO_ADMIN_ACCESS_REASON
            : "Confirm you’re creating this for the customer",
      }}
    >
      {currentEnvironment && (
        <EnvironmentCard
          label="You’re working in"
          projectName={currentEnvironment.project.name}
          envName={currentEnvironment.name}
        />
      )}
      <RadioGroup
        value={data.target}
        onValueChange={(value) => {
          updateData({ target: value as AddTarget });
          setNameError(null);
        }}
      >
        <Radio
          value="environment"
          label="An environment in an existing project"
          description="For a staging or test version of a site you already track."
        />
        <Radio
          value="project"
          label="A new project"
          description={`For a new site or domain. It starts with ${NEW_PROJECT_ENVIRONMENTS.join(" and ")} environments.`}
        />
      </RadioGroup>
      <div className="flex flex-col gap-4">
        {!isProject && (
          <Select
            options={projects}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.ID ?? ""}
            rootProps={{
              value: projectID ?? undefined,
              onValueChange: (value) => {
                updateData({ projectID: value });
                setNameError(null);
              },
            }}
            triggerProps={{ "aria-label": "Project" }}
            insetLabel="Project:"
            error={
              projectMissing
                ? "That project no longer exists. Choose another one."
                : undefined
            }
          />
        )}
        <Input
          label={isProject ? "Project name" : "Environment name"}
          description={
            isProject
              ? "Usually the site or domain, like “Marketing site”"
              : "Like “Staging” or “QA”"
          }
          value={data.name}
          onChange={(event) => {
            updateData({ name: event.target.value });
            setNameError(null);
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              canAdd !== false &&
              (!masquerading || masqueradeConfirmed) &&
              !saving
            ) {
              create();
            }
          }}
          error={
            nameError ? { type: "validate", message: nameError } : undefined
          }
        />
      </div>
      {trimmedName && !nameError && (isProject || project) && (
        <EnvironmentCard
          label={isProject ? "You’ll create" : "You’ll add"}
          projectName={isProject ? trimmedName : (project?.name ?? "")}
          envName={isProject ? NEW_PROJECT_ENVIRONMENTS[0] : trimmedName}
          highlighted
          isNew
        />
      )}
      {masquerading && (
        <Banner type="warning">
          <div className="flex flex-col items-start gap-2">
            <span>
              You’re masquerading, so this creates{" "}
              {isProject ? "a project" : "an environment"} in the customer’s
              account that they’ll see.
            </span>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={masqueradeConfirmed}
                onCheckedChange={(checked) =>
                  setMasqueradeConfirmed(checked === true)
                }
              />
              Create it in the customer’s account
            </label>
          </div>
        </Banner>
      )}
      {saveError && <Banner type="error">{saveError}</Banner>}
    </WizardStepLayout>
  );
}
